import { useEffect } from "react";
import { Button } from "./ui/button";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "./ui/sheet";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import { Calculator, Download, AlertTriangle } from "lucide-react";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

/**
 * Owner/manager spot-check tool — recomputes every employee balance from raw
 * inputs (time entries, payments, deductions) and surfaces any drift between
 * the audit and the live calculator. Pure UI; data fetch is driven by the
 * parent's `onLoad` callback.
 */
const BalanceAuditSheet = ({
  open,
  onOpenChange,
  data,
  loading,
  includeInactive,
  onToggleInactive,
  onLoad,
}) => {
  // Auto-fetch on first open.
  useEffect(() => {
    if (open && !data && !loading) {
      onLoad?.(includeInactive);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleExportCsv = () => {
    if (!data?.rows?.length) return;
    const headers = [
      "employee_id", "name", "pay_type", "rate", "hourly_entries",
      "hours_raw", "hours_payable", "policy_applied_entries",
      "salary_periods", "daily_entries",
      "earnings", "extras", "deductions", "paid", "balance",
      "live_balance", "matches_live", "diff_from_live",
    ];
    const lines = [headers.join(",")];
    for (const r of data.rows) {
      lines.push(headers.map((h) => {
        const v = r[h];
        if (v === null || v === undefined) return "";
        const s = String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `balance-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-5xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-[#0D9488]" />
            Balance Audit
          </SheetTitle>
          <SheetDescription>
            Recomputes every employee&apos;s balance from raw inputs and compares against the live calculator. Surfaces any drift.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-wrap items-center gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => onLoad?.(includeInactive)}
            disabled={loading}
            data-testid="audit-refresh-btn"
          >
            {loading ? "Recomputing…" : "Recompute"}
          </Button>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => {
                onToggleInactive?.(e.target.checked);
                onLoad?.(e.target.checked);
              }}
              data-testid="audit-include-inactive"
            />
            Include inactive / terminated
          </label>
          <Button
            variant="outline"
            onClick={handleExportCsv}
            disabled={!data?.rows?.length}
            className="ml-auto"
            data-testid="audit-export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>

        {data && (
          <div className="mt-6 space-y-4">
            {/* Drift summary */}
            {data.drift_count > 0 ? (
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 flex items-start gap-2" data-testid="audit-drift-banner">
                <AlertTriangle className="w-5 h-5 mt-0.5" />
                <div>
                  <p className="font-semibold">{data.drift_count} employee{data.drift_count === 1 ? "" : "s"} show drift</p>
                  <p className="text-xs">Highlighted rows below have a mismatch between the audit and the live balance.</p>
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800" data-testid="audit-allmatch-banner">
                ✓ All {data.rows.length} balances match the live calculator (no drift detected).
              </div>
            )}

            {/* Totals row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="p-3 bg-slate-50 rounded-md">
                <div className="text-xs text-slate-500 uppercase">Total Earnings</div>
                <div className="text-lg font-bold text-[#0A192F]">{fmt(data.totals.earnings)}</div>
              </div>
              <div className="p-3 bg-slate-50 rounded-md">
                <div className="text-xs text-slate-500 uppercase">Total Paid</div>
                <div className="text-lg font-bold text-[#0D9488]">{fmt(data.totals.paid)}</div>
              </div>
              <div className="p-3 bg-slate-50 rounded-md">
                <div className="text-xs text-slate-500 uppercase">Total Deductions</div>
                <div className="text-lg font-bold text-[#E11D48]">−{fmt(data.totals.deductions)}</div>
              </div>
              <div className="p-3 bg-slate-50 rounded-md">
                <div className="text-xs text-slate-500 uppercase">Outstanding</div>
                <div className="text-lg font-bold text-[#E11D48]">{fmt(data.totals.balance)}</div>
              </div>
            </div>

            {/* Detailed table */}
            <div className="border border-slate-200 rounded-md overflow-x-auto" data-testid="audit-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase">Employee</TableHead>
                    <TableHead className="text-xs uppercase">Type</TableHead>
                    <TableHead className="text-xs uppercase text-right">Rate</TableHead>
                    <TableHead className="text-xs uppercase text-right">Hours (raw / payable)</TableHead>
                    <TableHead className="text-xs uppercase text-right">Earnings</TableHead>
                    <TableHead className="text-xs uppercase text-right">Extras</TableHead>
                    <TableHead className="text-xs uppercase text-right">Deductions</TableHead>
                    <TableHead className="text-xs uppercase text-right">Paid</TableHead>
                    <TableHead className="text-xs uppercase text-right">Audit Balance</TableHead>
                    <TableHead className="text-xs uppercase text-right">Δ vs Live</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-slate-500">No employees to audit.</TableCell>
                    </TableRow>
                  ) : data.rows.map((r) => (
                    <TableRow
                      key={r.employee_id}
                      data-testid={`audit-row-${r.employee_id}`}
                      className={!r.matches_live ? "bg-amber-50" : ""}
                    >
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="capitalize text-xs">{r.pay_type}</TableCell>
                      <TableCell className="text-right text-xs">{fmt(r.rate)}</TableCell>
                      <TableCell className="text-right text-xs font-mono">
                        {r.pay_type === "hourly"
                          ? `${r.hours_raw}h / ${r.hours_payable}h`
                          : r.pay_type === "salaried"
                          ? `${r.salary_periods} periods`
                          : `${r.daily_entries} days`}
                      </TableCell>
                      <TableCell className="text-right">{fmt(r.earnings)}</TableCell>
                      <TableCell className="text-right text-[#10B981]">{r.extras > 0 ? `+${fmt(r.extras)}` : "—"}</TableCell>
                      <TableCell className="text-right text-[#E11D48]">{r.deductions > 0 ? `−${fmt(r.deductions)}` : "—"}</TableCell>
                      <TableCell className="text-right text-[#0D9488]">−{fmt(r.paid)}</TableCell>
                      <TableCell className={`text-right font-semibold ${r.balance > 0 ? "text-[#E11D48]" : "text-[#10B981]"}`}>
                        {fmt(r.balance)}
                      </TableCell>
                      <TableCell className={`text-right text-xs ${r.matches_live ? "text-slate-400" : "text-amber-700 font-bold"}`}>
                        {r.matches_live ? "✓ match" : fmt(r.diff_from_live)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {!data && loading && (
          <div className="py-16 text-center text-slate-500">Recomputing balances for all employees…</div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default BalanceAuditSheet;
